import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { cachedGet, invalidateCachedGet } from '../../services/requestCache';
import { useAuth } from '../../context/AuthContext';
import { getUserInitials } from '../../utils/userInitials';
import { getAiVisCssVars } from './aivisTheme';
import {
  AiVisBrandsIcon,
  AiVisCompetitorsIcon,
  AiVisContentStrategyIcon,
  AiVisDomainsIcon,
  AiVisEnrichmentIcon,
  AiVisFanOutIcon,
  AiVisHomeIcon,
  AiVisMembersIcon,
  AiVisOverviewIcon,
  AiVisPromptsIcon,
  AiVisResponsesIcon,
  AiVisSettingsNavIcon,
  AiVisSourcesIcon,
  AiVisSystemOverviewIcon,
} from './AiVisNavIcons';
import CampaignDropdown from './CampaignDropdown';
import { canAccessBrands, hasAdminConsoleAccess, isCampaignViewer, isSystemAdmin, sidebarRoleLabel } from './aivisRoles';
import './ai-vis.css';

const ORG_KEY = 'aivis_selected_org_id';

/**
 * Build an ordered list of logo URLs to try for an org.
 * Priority: backend-stored logo_url (same mechanism as client_logo_url for campaigns)
 *           → locally-hosted asset for the two built-in organizations.
 * The frontend never calls Clearbit directly — that is handled server-side.
 */
function orgLogoSources(name, logoUrl) {
  const sources = [];
  if (logoUrl) sources.push(logoUrl);
  // Hard-coded local fallbacks for the two built-in orgs (in case the stored URL expires)
  const n = (name || '').toLowerCase();
  if (n.includes('visibility labs')) sources.push('/visibilitylabs-mark.png');
  if (n.includes('180 marketing') || n.includes('180marketing')) sources.push('/Site-logo-180.png');
  return sources;
}

/**
 * Try each source URL in turn; falls back to the initial letter when all fail.
 * Resets to the first source whenever the org changes.
 */
function OrgLogoOrInitial({ name, logoUrl, logoClassName, initialClassName }) {
  const sources = orgLogoSources(name, logoUrl);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [name, logoUrl]);
  if (idx < sources.length) {
    return (
      <img
        src={sources[idx]}
        alt=""
        className={logoClassName}
        loading="lazy"
        decoding="async"
        onError={() => setIdx((i) => i + 1)}
      />
    );
  }
  return (
    <span className={initialClassName} aria-hidden>
      {(name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * Inline logo with the same waterfall — renders nothing when all sources fail.
 * Used inside the expanded trigger and the dropdown list items.
 */
function OrgLogo({ name, logoUrl, className }) {
  const sources = orgLogoSources(name, logoUrl);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [name, logoUrl]);
  if (idx >= sources.length) return null;
  return (
    <img
      src={sources[idx]}
      alt=""
      className={className}
      loading="lazy"
      decoding="async"
      onError={() => setIdx((i) => i + 1)}
    />
  );
}

function OrgDropdown({ orgs, selectedId, onSelectId, collapsed }) {
  const [open, setOpen] = useState(false);
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelClosing, setPanelClosing] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState({});

  const positionPanel = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const minW = 260;
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(minW, rect.width),
      zIndex: 10001,
    });
  }, []);

  useLayoutEffect(() => {
    if (!panelMounted) return;
    positionPanel();
  }, [panelMounted, collapsed, positionPanel]);

  useEffect(() => {
    if (!panelMounted) return;
    const upd = () => positionPanel();
    window.addEventListener('scroll', upd, true);
    window.addEventListener('resize', upd);
    return () => {
      window.removeEventListener('scroll', upd, true);
      window.removeEventListener('resize', upd);
    };
  }, [panelMounted, positionPanel]);

  const closePanel = useCallback(() => {
    setPanelClosing(true);
    setOpen(false);
  }, []);

  const openPanel = useCallback(() => {
    setPanelClosing(false);
    setPanelMounted(true);
    setOpen(true);
  }, []);

  function handlePanelAnimationEnd() {
    if (panelClosing) {
      setPanelMounted(false);
      setPanelClosing(false);
    }
  }

  useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      closePanel();
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [closePanel]);

  const selected = useMemo(
    () => orgs.find((o) => String(o.id) === String(selectedId)),
    [orgs, selectedId]
  );

  const label = selected ? selected.name : 'Select organization';

  const panel = panelMounted ? createPortal(
    <div
      ref={panelRef}
      className={`aivis-org-dd-panel${panelClosing ? ' aivis-org-dd-panel--closing' : ''}`}
      style={{ ...getAiVisCssVars(), ...panelStyle }}
      role="presentation"
      onAnimationEnd={handlePanelAnimationEnd}
    >
      <ul className="aivis-org-dd-list" role="listbox" aria-label="Organizations">
        {orgs.map((o) => {
          const isSel = String(o.id) === String(selectedId);
          return (
            <li key={o.id} role="option" aria-selected={isSel}>
              <button
                type="button"
                className={`aivis-org-dd-item${isSel ? ' aivis-org-dd-item--active' : ''}`}
                onClick={() => { onSelectId(String(o.id)); closePanel(); }}
              >
                <span className="aivis-org-dd-item-row">
                  <OrgLogo name={o.name} logoUrl={o.logo_url} className="aivis-org-dd-item-logo" />
                  <span className="aivis-org-dd-item-name">{o.name}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>,
    document.body
  ) : null;

  if (collapsed) {
    return (
      <div className="aivis-org-dd" ref={wrapRef}>
        <button
          ref={triggerRef}
          type="button"
          className="aivis-campaign-dd-trigger aivis-campaign-dd-trigger-collapsed"
          title={label}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={(e) => { e.stopPropagation(); if (open) closePanel(); else openPanel(); }}
        >
          {selected ? (
            <OrgLogoOrInitial
              name={selected.name}
              logoUrl={selected.logo_url}
              logoClassName="aivis-campaign-dd-trigger-logo"
              initialClassName="aivis-campaign-dd-trigger-initial"
            />
          ) : (
            /* Placeholder org icon when nothing selected */
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10" />
            </svg>
          )}
        </button>
        {panel}
      </div>
    );
  }

  return (
    <div className="aivis-org-dd" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="aivis-campaign-dd-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        onClick={(e) => { e.stopPropagation(); if (open) closePanel(); else openPanel(); }}
      >
        {selected?.name && (
          <OrgLogo name={selected.name} logoUrl={selected.logo_url} className="aivis-campaign-dd-trigger-logo-inline" />
        )}
        <span className="aivis-campaign-dd-trigger-text">{label}</span>
        <span className="aivis-campaign-dd-chevron" aria-hidden>
          <svg
            className="aivis-campaign-dd-chevron-svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {panel}
    </div>
  );
}

const SELECTED_KEY = 'aivis_selected_campaign_id';
const COLLAPSE_KEY = 'aivis_sidebar_collapsed';

function navLinkClass({ isActive }, collapsed) {
  return [isActive ? 'aivis-nav-active' : undefined, collapsed ? 'aivis-nav-collapsed' : undefined]
    .filter(Boolean)
    .join(' ') || undefined;
}

function SidebarLink({ to, end, collapsed, title, Icon, label }) {
  return (
    <NavLink to={to} className={(p) => navLinkClass(p, collapsed)} end={end} title={title}>
      {collapsed ? (
        <span className="aivis-nav-icon-chip">
          <Icon className="aivis-nav-svg" title={title} />
        </span>
      ) : (
        <span className="aivis-nav-link-inner">
          <Icon className="aivis-nav-svg-inline" title={title} />
          <span>{label}</span>
        </span>
      )}
    </NavLink>
  );
}

function NavSection({ title, collapsed, children }) {
  return (
    <div className="aivis-nav-section">
      {!collapsed ? <div className="aivis-nav-section-title">{title}</div> : <div className="aivis-nav-section-rule" role="separator" />}
      <div className="aivis-nav-section-links">{children}</div>
    </div>
  );
}

export default function AiVisLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Organizations
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(
    () => localStorage.getItem(ORG_KEY) || ''
  );

  // URL param is the most authoritative source on initial load; then localStorage
  const campaignIdFromUrl = new URLSearchParams(location.search).get('campaign');
  const [selectedId, setSelectedId] = useState(
    () => campaignIdFromUrl || localStorage.getItem(SELECTED_KEY) || ''
  );

  const canCompany = hasAdminConsoleAccess(user);
  const canCreateCampaign = !isCampaignViewer(user);
  const showBrands = canAccessBrands(user);
  const showContentStrategy = orgs.find((o) => String(o.id) === String(selectedOrgId))?.name === 'Visibility Labs';

  useEffect(() => {
    function onDocClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const refreshOrgs = useCallback(async () => {
    try {
      const { data } = await api.get('/aivis/organizations/');
      const list = data.results ?? data;
      setOrgs(Array.isArray(list) ? list : []);
    } catch {
      setOrgs([]);
    }
  }, []);

  // Fetch organizations once on mount
  useEffect(() => { refreshOrgs(); }, [refreshOrgs]);

  const onSelectOrgId = useCallback((id) => {
    setSelectedOrgId(id);
    if (id) localStorage.setItem(ORG_KEY, id);
    else localStorage.removeItem(ORG_KEY);
  }, []);

  const refreshCampaigns = useCallback(async (orgId) => {
    const filterOrg = orgId !== undefined ? orgId : selectedOrgId;
    setCampaignsLoaded(false);
    try {
      const params = new URLSearchParams();
      if (filterOrg) params.set('organization', filterOrg);
      const query = params.toString();
      const cacheKey = `campaign-options:${filterOrg || 'all'}`;
      invalidateCachedGet(cacheKey);
      const data = await cachedGet(
        cacheKey,
        `/aivis/campaigns/options/${query ? `?${query}` : ''}`,
        { maxAge: 30000 },
      );
      setCampaigns(data.results ?? []);
    } catch {
      setCampaigns([]);
    } finally {
      setCampaignsLoaded(true);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    refreshCampaigns();
  }, [refreshCampaigns]);

  // Only resolve the selected campaign AFTER the first fetch completes.
  // This prevents the empty-array interim state from clearing a URL-specified ID.
  useEffect(() => {
    if (!campaignsLoaded) return;

    if (!campaigns.length) {
      // User truly has no campaigns
      setSelectedId('');
      localStorage.removeItem(SELECTED_KEY);
      return;
    }

    const exists = campaigns.some((c) => String(c.id) === String(selectedId));
    if (!exists) {
      // The stored/URL ID doesn't match any campaign → fall back to first
      const next = String(campaigns[0].id);
      setSelectedId(next);
      localStorage.setItem(SELECTED_KEY, next);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, campaignsLoaded]); // intentionally exclude selectedId to avoid loops

  // Sync URL query param → selectedId when navigating between pages (e.g. after wizard creates a campaign)
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('campaign');
    if (id && id !== selectedId) {
      setSelectedId(id);
      localStorage.setItem(SELECTED_KEY, id);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync selectedId → URL query param + localStorage whenever it changes OR the path changes
  useEffect(() => {
    if (!selectedId) return;
    const params = new URLSearchParams(location.search);
    if (params.get('campaign') !== selectedId) {
      params.set('campaign', selectedId);
      // Preserve location.state so deep-link state (e.g. initialSearch from Top Domains) isn't wiped
      navigate(`${location.pathname}?${params.toString()}`, { replace: true, state: location.state });
    }
    localStorage.setItem(SELECTED_KEY, selectedId);
  }, [selectedId, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectCampaignId = useCallback((id) => {
    const s = id ? String(id) : '';
    setSelectedId(s);
    if (s) localStorage.setItem(SELECTED_KEY, s);
    else localStorage.removeItem(SELECTED_KEY);
  }, []);

  const toggleCollapse = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  const outletContext = useMemo(
    () => ({
      campaigns,
      campaignsLoaded,
      refreshCampaigns,
      selectedCampaignId: selectedId,
      setSelectedCampaignId: onSelectCampaignId,
      orgs,
      refreshOrgs,
      selectedOrgId,
      setSelectedOrgId: onSelectOrgId,
      user,
    }),
    [campaigns, campaignsLoaded, refreshCampaigns, selectedId, onSelectCampaignId, orgs, refreshOrgs, selectedOrgId, onSelectOrgId, user]
  );

  const initials = getUserInitials(user);
  const displayName = user?.display_name || user?.email || user?.username || '';
  const roleLine = sidebarRoleLabel(user);

  return (
    <div className="aivis-app" style={getAiVisCssVars()}>
      <aside className={`aivis-sidebar ${collapsed ? 'aivis-sidebar-collapsed' : ''}`}>
        <div className="aivis-sidebar-header">
          <div className="aivis-sidebar-header-row">
            <button
              type="button"
              className="aivis-sidebar-toggle"
              onClick={toggleCollapse}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              <svg
                className="aivis-sidebar-toggle-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <rect x="3.5" y="4.5" width="17" height="15" rx="3" stroke="currentColor" strokeWidth="1.5" />
                <rect x="6.5" y="7" width="4.5" height="10" rx="1" fill="currentColor" />
              </svg>
            </button>
            {!collapsed && (
              <Link to="/ai-vis/home" className="aivis-brand">
                <span>180Marketing</span>
                <span className="aivis-brand-badge">AI-Vis</span>
              </Link>
            )}
          </div>
        </div>

        {orgs.length > 0 && (
          <div className="aivis-sidebar-org-slot">
            <OrgDropdown
              orgs={orgs}
              selectedId={selectedOrgId}
              onSelectId={onSelectOrgId}
              collapsed={collapsed}
            />
          </div>
        )}

        <div className="aivis-sidebar-campaign-slot">
          <CampaignDropdown
            campaigns={campaigns}
            selectedId={selectedId}
            onSelectId={onSelectCampaignId}
            canCreateCampaign={canCreateCampaign}
            collapsed={collapsed}
          />
        </div>

        <nav className="aivis-nav aivis-nav-stack">
          {/* CAMPAIGN — Overview hidden from campaign viewers (clients) for now */}
          {!isCampaignViewer(user) || showContentStrategy ? (
            <NavSection title="Campaign" collapsed={collapsed}>
              {!isCampaignViewer(user) ? (
                <SidebarLink to="/ai-vis/overview" collapsed={collapsed} title="Overview" Icon={AiVisOverviewIcon} label="Overview" />
              ) : null}
              {showContentStrategy ? (
                <SidebarLink to="/ai-vis/content-strategy" collapsed={collapsed} title="Content Strategy" Icon={AiVisContentStrategyIcon} label="Content Strategy" />
              ) : null}
            </NavSection>
          ) : null}

          {/* REPORTING */}
          <NavSection title="Reporting" collapsed={collapsed}>
            <SidebarLink to="/ai-vis/home" end collapsed={collapsed} title="AI Visibility" Icon={AiVisHomeIcon} label="AI Visibility" />
            <SidebarLink to="/ai-vis/prompts" collapsed={collapsed} title="Prompts" Icon={AiVisPromptsIcon} label="Prompts" />
            <SidebarLink to="/ai-vis/domains" collapsed={collapsed} title="Cited Domains" Icon={AiVisDomainsIcon} label="Cited Domains" />
            <SidebarLink to="/ai-vis/sources" collapsed={collapsed} title="Cited URLs" Icon={AiVisSourcesIcon} label="Cited URLs" />
            {!isCampaignViewer(user) ? (
              <>
                <SidebarLink to="/ai-vis/competitors" collapsed={collapsed} title="Competitors" Icon={AiVisCompetitorsIcon} label="Competitors" />
                <SidebarLink to="/ai-vis/responses" collapsed={collapsed} title="Responses" Icon={AiVisResponsesIcon} label="Responses" />
                <SidebarLink to="/ai-vis/fan-out-queries" collapsed={collapsed} title="Fan-Out Queries" Icon={AiVisFanOutIcon} label="Fan-Out Queries" />
              </>
            ) : null}
          </NavSection>

          {/* SETTINGS — admins only */}
          {canCompany ? (
            <NavSection title="Settings" collapsed={collapsed}>
              {showBrands ? (
                <SidebarLink to="/ai-vis/brands" collapsed={collapsed} title="Clients" Icon={AiVisBrandsIcon} label="Clients" />
              ) : null}
              <SidebarLink
                to="/ai-vis/settings"
                collapsed={collapsed}
                title="Organizations"
                Icon={AiVisSettingsNavIcon}
                label="Organizations"
              />
              <SidebarLink to="/ai-vis/members" collapsed={collapsed} title="Members" Icon={AiVisMembersIcon} label="Members" />
              {isSystemAdmin(user) ? (
                <SidebarLink
                  to="/ai-vis/system-wide-overview"
                  collapsed={collapsed}
                  title="System Overview"
                  Icon={AiVisSystemOverviewIcon}
                  label="System Overview"
                />
              ) : null}
              {isSystemAdmin(user) ? (
                <SidebarLink
                  to="/ai-vis/enrichment-tracking"
                  collapsed={collapsed}
                  title="Enrichment Tracking"
                  Icon={AiVisEnrichmentIcon}
                  label="Enrichment Tracking"
                />
              ) : null}
            </NavSection>
          ) : null}
        </nav>

        <div className="aivis-sidebar-footer">
          <div className="aivis-user-menu-wrap" ref={userMenuRef}>
            <button
              type="button"
              className="aivis-user-menu-trigger"
              onClick={(e) => {
                e.stopPropagation();
                setUserMenuOpen((o) => !o);
              }}
              aria-expanded={userMenuOpen}
            >
              <span className="aivis-user-avatar" aria-hidden="true">
                {initials}
              </span>
              {!collapsed && (
                <span className="aivis-user-menu-text">
                  <span className="aivis-user-menu-name">{displayName}</span>
                  {roleLine ? <span className="aivis-user-menu-role">{roleLine}</span> : null}
                </span>
              )}
              <span className={`aivis-user-menu-chevron${userMenuOpen ? ' aivis-user-menu-chevron--open' : ''}`} aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
            <div className={`aivis-user-menu-dropdown${userMenuOpen ? ' aivis-user-menu-dropdown--open' : ''}`} aria-hidden={!userMenuOpen}>
              <button
                type="button"
                className="aivis-user-menu-item"
                tabIndex={userMenuOpen ? 0 : -1}
                onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </aside>

      <main className="aivis-main">
        <Outlet context={outletContext} />
      </main>
    </div>
  );
}
